import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import LoaderComponent from '@components/LoaderComponent';
import TeamMember from '@components/TeamMember';
import { toastError, toastSuccess } from '@components/Toast';

import { fetchAllUsersByTypes } from '@store/orders/actions';
import { getAllUsersByTypes } from '@store/reducers';

import ApiClient from '@lib/apiClient';

const AddMember = ({ currentStepDetails, orderId }) => {
	const [usersByTypeList, setUsersByTypeList] = useState([]);
	const [showAddMemberModal, setShowAddMemberModal] = useState(false);
	const [userResponseList, setUserResponseList] = useState('');
	const [currentWorkflowStep, setCurrentWorkflowStep] = useState([]);
	const [searchUserSuggestions, setSearchUserSuggestions] = useState([]);
	const [searchUserText, setSearchUserText] = useState('');
	const [loading, setLoading] = useState(false);
	const PRO_PIC_DEFAULT = `${process.env.PUBLIC_URL}/images/pro_pic_default.svg`;
	const dispatch = useDispatch();
	const allUsersByTypes = useSelector(getAllUsersByTypes);
	const AddNewMemberButton = useRef(null);
	const AddNewMemberModal = useRef(null);
	const moreMember = 4;

	const memberData = {
		usersByTypeList,
		searchUserText,
		searchUserSuggestions,
		memberList: userResponseList
	};

	useEffect(() => {
		dispatch(fetchAllUsersByTypes());
	}, []);

	useEffect(() => {
		setUsersByTypeList(allUsersByTypes);
	}, [allUsersByTypes]);

	useEffect(() => {
		if (currentStepDetails?.memberResponseList) {
			setUserResponseList(currentStepDetails.memberResponseList);
		}
		setCurrentWorkflowStep(currentStepDetails?.workflowStep);

		document.addEventListener('click', handleClickOutside, { capture: true });
		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, [currentStepDetails]);

	const handleClickOutside = (event) => {
		if (
			AddNewMemberModal.current &&
			!AddNewMemberModal.current.contains(event.target) &&
			AddNewMemberButton.current &&
			!AddNewMemberButton.current.contains(event.target)
		) {
			setShowAddMemberModal(false);
		}
	};

	const addUserToOrder = async (user) => {
		setLoading(true);
		const body = {
			entityId: orderId,
			entityType: 'ORDER',
			step: currentWorkflowStep,
			type: 'ORDER_PLACEMENT_WORKFLOW'
		};
		let newUserResponseList = [...userResponseList];
		let params = `?memberIds=${user.id}`;
		await ApiClient.POST('addWorkflowMember', body, params)
			.then(({ data }) => {
				if (data) {
					setLoading(false);
					if (userResponseList && userResponseList.length) {
						let flag = true;
						userResponseList.forEach((item) => {
							if (item.id === user.id) {
								flag = false;
							}
						});
						if (flag) {
							toastSuccess(data.message);
							newUserResponseList = [...userResponseList, user];
						} else {
							toastSuccess('Collection already shared');
						}
					} else {
						newUserResponseList = [user];
					}
					setUserResponseList(newUserResponseList);
				}
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const removeUserFromOrder = async (user) => {
		setLoading(true);
		const body = {
			entityId: orderId,
			entityType: 'ORDER',
			step: currentWorkflowStep,
			type: 'ORDER_PLACEMENT_WORKFLOW'
		};
		let newUserResponseList = [...userResponseList];
		let params = `?memberIds=${user.id}`;
		await ApiClient.POST('removeWorkflowMember', body, params)
			.then(({ data }) => {
				if (data) {
					setLoading(false);
					if (userResponseList && userResponseList.length) {
						newUserResponseList = userResponseList.filter((item) => item.id !== user.id);
					}
					setUserResponseList(newUserResponseList);
					toastSuccess(data.message);
				}
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const getSearchSuggestions = async (inputText) => {
		setLoading(true);
		let params = `?email=${inputText}`;
		await ApiClient.GET('getUserSuggestions', params)
			.then(({ data }) => {
				setLoading(false);
				if (data) {
					setSearchUserSuggestions(data);
				}
			})
			.catch(({ response }) => {
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onChangeTeam = (e) => {
		setSearchUserText(e.target.value);
		getSearchSuggestions(e.target.value);
	};

	const renderIcon = (user) => {
		if (user.profilePicDocument && user.profilePicDocument.docUrl) {
			return (
				<img
					key={user.id}
					onClick={() => setShowAddMemberModal(!showAddMemberModal)}
					src={user.profilePicDocument.docUrl}
					alt=""
					data-toggle="tooltip"
					data-placement="top"
					title={user.name}
				/>
			);
		}
		return (
			<img
				key={user.id}
				onClick={() => setShowAddMemberModal(!showAddMemberModal)}
				src={PRO_PIC_DEFAULT}
				alt=""
				data-toggle="tooltip"
				data-placement="top"
				title={user.name}
			/>
		);
	};

	const renderMemberIcon = (userResponseList) => {
		if (userResponseList.length > moreMember) {
			return userResponseList.slice(0, moreMember).map((user) => {
				return renderIcon(user);
			});
		} else {
			return userResponseList.map((user) => {
				return renderIcon(user);
			});
		}
	};

	return (
		<div className="px-0 add-buyer d-flex justify-content-end">
			<div className="position-relative">
				<div className="added-members">
					<div
						id="AddNewMember"
						ref={AddNewMemberButton}
						className={`add-new-member ${showAddMemberModal ? 'show' : ''}`}
					>
						<img
							src={`${process.env.PUBLIC_URL}/icons/add-people.svg`}
							alt="add"
							onClick={() => setShowAddMemberModal(!showAddMemberModal)}
						/>
					</div>

					{userResponseList && userResponseList ? (
						<>
							{renderMemberIcon(userResponseList)}
							{userResponseList.length > moreMember ? (
								<div className="more-people">
									<span onClick={() => setShowAddMemberModal(!showAddMemberModal)}>
										+{userResponseList.length - moreMember}
									</span>
								</div>
							) : (
								<></>
							)}
						</>
					) : (
						<></>
					)}
				</div>
				<div
					className={`add-people-popup no-padding-popup shadow ${showAddMemberModal ? 'show' : ''}`}
					ref={AddNewMemberModal}
				>
					<div className="close-add-people mb-3 d-block d-sm-none">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="20.941"
							height="20.941"
							viewBox="0 0 20.941 20.941"
						>
							<g
								id="Group_22803"
								data-name="Group 22803"
								transform="translate(2489.29 -478.941)"
							>
								<line
									id="Line_153"
									data-name="Line 153"
									x2="25.615"
									transform="translate(-2487.875 480.355) rotate(45)"
									fill="none"
									stroke="#21242b"
									strokeLinecap="round"
									strokeWidth="2"
								/>
								<line
									id="Line_154"
									data-name="Line 154"
									x2="25.615"
									transform="translate(-2469.763 480.355) rotate(135)"
									fill="none"
									stroke="#21242b"
									strokeLinecap="round"
									strokeWidth="2"
								/>
							</g>
						</svg>
					</div>
					<LoaderComponent loading={loading}>
						<TeamMember
							memberData={memberData}
							onAddMember={addUserToOrder}
							onRemoveMember={removeUserFromOrder}
							onChange={onChangeTeam}
						/>
					</LoaderComponent>
				</div>
			</div>
		</div>
	);
};

export default AddMember;
